import { Replay, SearchRounded, FilterAlt } from "@mui/icons-material";
import { Autocomplete, Box, Button, Grid, IconButton, TextField, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { SyntheticEvent, useCallback, useContext, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { setSnackbar } from "../../../util/state/globalAction";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../util/state/globalVariables";
import { getSapEnumConfiguration } from "../api/service/customerbaseApi";
import { customerSearchFiltersState, sapEnumState } from "../state/customerbaseState";
import { BLANK_OPTION, CBAuth, CBPartnerType, CustomerSearchFilters, IOSapEnum, YES_NO_OPTIONS, YES_NO_OPTIONS_BOOL } from "../types";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useHasScope } from "../../../config/globalConfig";
import { v4 } from "uuid";
import CBSearch from "./search/layout/CBSearch";
import CBOrganizationSearch from "./search/layout/CBOrganizationSearch";
import CBPersonSearch from "./search/layout/CBPersonSearch";

interface CustomerBaseSearchProps {
    activePartnerType: "all" | "person" | "organization"

}

const CustomerBaseSearch = ({ activePartnerType }: CustomerBaseSearchProps)  => {
    const theme = useTheme();
    const { dispatchGlobalData } = useContext(GlobalContext);
    const hasScope = useHasScope;
    const isOrganizationViewer = hasScope(CBAuth.VIEW_ORGANIZATION);
    const isPersonViewer = hasScope(CBAuth.VIEW_PERSON);
    const {t} = useTranslation('customerbase');
    const [showExtraFilters, setShowExtraFilters] = useState(false);
    const validationSchema = yup.object().shape({
        cbCustomerId: yup.string().nullable(true).trim(),
        taxCode: yup.string().nullable(true).trim().uppercase(),
        vatNumber: yup.string().nullable(true).trim(),
        drvLicenceNumber: yup.string().nullable(true).trim(),
        lastName: yup.string().nullable(true).trim(),
        firstName: yup.string().nullable(true).trim(),
        businessName: yup.string().nullable(true).trim(),
        email: yup
        .string().nullable(true).trim().lowercase()
        .email(t('message.validation.emailNotValid')),
        partnerType: yup.string().nullable().notRequired(),
        dossierStatus: yup.string().nullable().notRequired()
    });

    const form = useForm<CustomerSearchFilters>({
        defaultValues: {
            cbCustomerId: "",
            taxCode: "",
            vatNumber: "",
            drvLicenceNumber: "",
            lastName: "",
            firstName: "",
            email: "",
            businessName: "",
            customerType: null,
            role: [],
            vip: null,
            partnerType: activePartnerType == "all" ? 
                (isPersonViewer && isOrganizationViewer? null : (isPersonViewer ? CBPartnerType.PERSON : CBPartnerType.ORGANIZATION))
                :
                ( activePartnerType == "person" ? CBPartnerType.PERSON : CBPartnerType.ORGANIZATION),
            dossierStatus: "",
            digitalIdentity: null,
            digitalIdentityStatus: '',
            dossierOrigin: ''
        },
        resolver: yupResolver(validationSchema)
    });
    
    const sapEnum = useRecoilValue(sapEnumState);
    const setSapEnum = useSetRecoilState(sapEnumState);
    const setCustomerSearchFilters = useSetRecoilState(customerSearchFiltersState);


    const createCustomerSearchFilters = useCallback((params: any) => ({
        cbCustomerId: params.cbCustomerId || null,
        taxCode: params.taxCode || null,
        vatNumber: params.vatNumber || null,
        drvLicenceNumber: params.drvLicenceNumber || null,
        lastName: params.lastName || null,
        firstName: params.firstName || null,
        email: params.email || null,
        businessName: params.businessName || null,
        customerType: params.customerType || null,
        role: params.role || [],
        vip: params.vip || null,
        partnerType: params.partnerType || null,
        dossierStatus: params.dossierStatus || null,
        digitalIdentity: params.digitalIdentity,
        digitalIdentityStatus: params.digitalIdentityStatus || null,
        dossierOrigin: params.dossierOrigin || null
    }), []);

    const onReset = () => {
        form.reset();

        onSubmit(form.getValues());
    };

    const onAdvancedFiltersClick = () => {
        setShowExtraFilters(!showExtraFilters);
    };

    /*const onCloseFilter = () => {
        form.resetField("cbCustomerId");
        form.resetField("customerType");
        form.resetField("role");
        form.resetField("vip");
        form.resetField("partnerType");

        //setCustomerSearchFilters(createCustomerSearchFilters(form.getValues()));
    }*/

    const onSubmit = (params: any) => {
        console.log("Filters: ", params);
        
        if(showExtraFilters)
            setShowExtraFilters(false);

        setCustomerSearchFilters(createCustomerSearchFilters(params));
    }

    const sapEnumQuery = useQuery(['getSapEnumConfiguration'], getSapEnumConfiguration, 
    {
        enabled: false,   
        onSuccess: (data) => {
            console.log("SAP: ", data?.data);
            setSapEnum(data?.data);
        },
        onError: (error: any) => {
            console.log(error);

            let errorKey = "tryLater";
            if(error?.response?.data?.code) errorKey = error.response.data.code;
                
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t(`message.error.${errorKey}`),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    });

    useEffect(()=> {
        if(!Object.values(sapEnum).every(val => val.length > 0))
            sapEnumQuery.refetch();
    }, [sapEnum]);

    
    return (
        <form autoComplete="off" onSubmit={form.handleSubmit(onSubmit)}>
            <Box
                border="1px solid #DBE4E9"
                boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
                borderRadius="4px"
                padding={theme.spacing(1) + ' ' + theme.spacing(2)}
                marginBottom={theme.spacing(2)}
            >
                <FormProvider {...form}>
                    {activePartnerType == "all" &&
                        <CBSearch
                            showPartnerTypeFilter={isPersonViewer && isOrganizationViewer}
                            showExtraFilters={showExtraFilters}
                            onAdvancedFiltersClick={onAdvancedFiltersClick}
                            onReset={onReset} 
                        />
                    }
                    {activePartnerType == "organization" && <CBOrganizationSearch onReset={onReset} />}
                </FormProvider>
            </Box>
        </form>
    );

}

export default CustomerBaseSearch;